import { GraphQLClient, gql } from 'graphql-request'

import type { Contact, WebDropinAdditionalFields } from '@/lib/types'

const TOKEN_PREFIX = 'JWT'

const publicTransactionQuery = gql`
  query ($transactionId: UUID!) {
    publicTransaction(transactionId: $transactionId) {
      data {
        customerCreditCard {
          cardholderName
        }
      }
    }
  }
`

interface PublicTransactionResponse {
  publicTransaction?: {
    data?: {
      customerCreditCard?: {
        cardholderName?: string | null
      } | null
    } | null
  } | null
}

/**
 * Fetches the cardholder name from the public (unauthenticated) transaction
 * query. Used on /hpp-session after a successful payment to enrich the
 * card-payment-completed postMessage. Returns null on any error or absent field
 * so the caller can safely fall back to the bare message shape.
 */
export async function fetchCardholderName(origin: string, transactionId: string): Promise<string | null> {
  const client = new GraphQLClient(`${origin}/graphql/`)
  try {
    const response = await client.request<PublicTransactionResponse>(publicTransactionQuery, { transactionId })
    const name = response.publicTransaction?.data?.customerCreditCard?.cardholderName
    const trimmedName = name?.trim()
    return trimmedName || null
  } catch {
    return null
  }
}

const webDropinAdditionalFieldsQuery = gql`
  query ($transactionId: UUID!) {
    publicTransaction(transactionId: $transactionId) {
      data {
        company {
          settings {
            webDropinAdditionalFields {
              postalCode
              fullBillingAddress
            }
          }
        }
      }
    }
  }
`

interface WebDropinAdditionalFieldsResponse {
  publicTransaction?: {
    data?: {
      company?: {
        settings?: {
          webDropinAdditionalFields?: WebDropinAdditionalFields | null
        } | null
      } | null
    } | null
  } | null
}

/**
 * Fetches the company's web-drop-in additional-fields settings from the
 * public (unauthenticated) transaction query. Used at render time on the
 * PAYMENT routes to derive the checkout field mode (see
 * lib/checkout-field-mode.ts::resolveCheckoutFieldMode). Returns null on any
 * error — missing field on the schema (BE not deployed yet), network
 * failure, GraphQL errors, or absent data — so the caller can safely fail
 * open to the 'default' mode (JET-2000).
 */
export async function fetchWebDropinAdditionalFields(
  origin: string,
  transactionId: string
): Promise<WebDropinAdditionalFields | null> {
  const client = new GraphQLClient(`${origin}/graphql/`)
  try {
    const response = await client.request<WebDropinAdditionalFieldsResponse>(webDropinAdditionalFieldsQuery, {
      transactionId,
    })
    return response.publicTransaction?.data?.company?.settings?.webDropinAdditionalFields ?? null
  } catch {
    return null
  }
}

const getTransactionQuery = gql`
  query ($transactionId: UUID!) {
    transaction(transactionId: $transactionId) {
      data {
        contact {
          identifier
        }
      }
      authorizationError {
        message
        helpMessage
      }
      authenticationError {
        message
        helpMessage
      }
      notFoundError {
        message
        helpMessage
      }
      fieldErrors {
        field
        message
        helpMessage
      }
    }
  }
`

const payDebitViaCreditCardMutation = gql`
  mutation (
    $transactionId: UUID!
    $customerCreditCardId: UUID!
  ) {
    payDebitViaCreditCard(
      input: {
        debitId: $transactionId
        customerCreditCardId: $customerCreditCardId
      }
    ) {
      message
      transaction {
        identifier
        states {
          state
        }
        customerCreditCard {
          brand
          last4
        }
        creditCardFee
      }
      notFoundError {
        message
      }
      fieldErrors {
        field
        message
        helpMessage
      }
    }
  }
`

interface GetTransactionResponse {
  transaction: {
    data?: { contact?: Contact }
    notFoundError?: { message: string; helpMessage?: string }
    fieldErrors?: Array<{ field: string; message: string }>
  }
}

interface PayDebitResponse {
  payDebitViaCreditCard: {
    message?: string
    transaction?: {
      identifier: string
      states: Array<{ state: string }>
      customerCreditCard: { brand: string; last4: string }
      creditCardFee?: number
    }
    notFoundError?: { message: string }
    fieldErrors?: Array<{ field: string; message: string }>
  }
}

/**
 * GraphQL client for internal-api transaction operations. Ported from the
 * webpack-era `InternalTransactionsApiClient` + `GraphQL.createClient`.
 *
 * Endpoint: `${origin}/graphql/`. Auth header is `JWT <token>` (same prefix
 * the rest of the internal-api uses, except `/auth-token` and the tokenization
 * session creation — see specs/app/index.md::Internal API contract).
 */
export class TransactionsClient {
  private readonly client: GraphQLClient

  constructor(origin: string, token: string) {
    this.client = new GraphQLClient(`${origin}/graphql/`, {
      headers: { Authorization: `${TOKEN_PREFIX} ${token}` },
    })
  }

  async getContact(transactionId: string): Promise<Contact> {
    const response = await this.client.request<GetTransactionResponse>(getTransactionQuery, { transactionId })
    const contact = response.transaction?.data?.contact
    if (!contact) {
      throw new Error(`No contact found for transaction ${transactionId}`)
    }
    return contact
  }

  async payTransactionViaCreditCard(transactionId: string, customerCreditCardId: string): Promise<PayDebitResponse> {
    return this.client.request<PayDebitResponse>(payDebitViaCreditCardMutation, {
      transactionId,
      customerCreditCardId,
    })
  }
}
